import { useMemo } from "react";
import { FiInfo, FiMapPin } from "react-icons/fi";
import moment from "moment-timezone";
import ReactSelect, { components, MenuListProps } from "react-select";
import makeAnimated from "react-select/animated";
import ReactTooltip from "react-tooltip";
import { FormLabel } from "../FormLabel";
import FormikFieldError from "../FormikFieldError";
import { createSingleSelectCustomTestIdComponents } from "utils/selectHelper";
import { getReactSelectClassNames, getInputWrapperClasses } from "./inputStyles";
import InfoTooltip from "newui/components/InfoTooltip";

const animatedComponents = makeAnimated();

export interface SelectOption {
  label: string;
  value: string;
}

export interface BaseTimeZonePickerWithLabelProps {
  name?: string;
  className?: string;
  disabled?: boolean;
  errorText?: string;
  guideLine?: string;
  hasError?: boolean;
  label?: string;
  required?: boolean;
  restrictTo?: string;
  testId?: string;
  value?: string;
  onChange?: (timezone: string) => void;
}

export const TimeZonePickerWithLabel: React.FC<BaseTimeZonePickerWithLabelProps> = ({
  className = "",
  disabled,
  errorText,
  guideLine,
  hasError = false,
  label,
  name,
  required,
  restrictTo = "Australia",
  testId,
  value,
  onChange,
}) => {
  const hasInputError = !!errorText || hasError;

  const timezones = useMemo(() => {
    return moment.tz.names()
      .filter((tz) => {
        if (!restrictTo) {
          return !tz.startsWith("Etc/");
        }
        return tz.includes(restrictTo) && !tz.startsWith("Etc/");
      })
      .map((tz) => ({ value: tz, label: tz.replace(restrictTo, '').split('/').pop()?.replace(/_/g, ' ') }));
  }, [restrictTo]);

  const selectedOption = useMemo(() => {
    if (!value) {
      return null;
    }
    return timezones.find((tz) => tz.value === value) || null;
  }, [value, timezones]);

  const handleChange = (option: SelectOption | null) => {
    if (onChange) {
      onChange(option?.value || "");
    }
  };

  const handleSetToMine = () => {
    const userTimezone = moment.tz.guess();
    if (onChange) {
      onChange(userTimezone);
    }
  };

  const customComponents = testId ? createSingleSelectCustomTestIdComponents(testId) : {};

  const MenuListWithButton = (props: MenuListProps<SelectOption>) => {
    return (
      <components.MenuList {...props}>
        <div
          className="px-4 py-2 border-b border-gray-200 bg-blue-50 cursor-pointer hover:bg-blue-100 flex items-center gap-2"
          onClick={handleSetToMine}
        >
          <FiMapPin className="text-blue-500" size={16} />
          <span className="text-gray-700 font-medium">Set to Mine</span>
        </div>
        {props.children}
      </components.MenuList>
    );
  };

  return (
    <div
      className={getInputWrapperClasses(hasInputError, false, className)}
      data-testid={testId}
    >
      {label && (
        <FormLabel htmlFor={name} label={label} required={required}>
          {guideLine && (
            <div className="inline-block">
              <InfoTooltip tooltipText={guideLine} />
            </div>
          )}
        </FormLabel>
      )}

      <ReactSelect
        name={name}
        unstyled
        classNames={getReactSelectClassNames(hasInputError, !!selectedOption?.value, disabled)}
        closeMenuOnSelect={true}
        components={{
          ...animatedComponents,
          ...customComponents,
          MenuList: MenuListWithButton,
        }}
        onChange={handleChange}
        value={selectedOption}
        options={timezones}
        isDisabled={disabled}
        placeholder="Select timezone"
      />

      <FormikFieldError text={errorText} />
    </div>
  );
};
